import { router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import Header from "../Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const formatCoin = (value) =>
    new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const calculateInvoiceItemCoin = (item) => {
    const variant = item?.variant ?? null;
    const isCustomVariant = Number(variant?.prod_var_type ?? 0) === 1;
    const itemValue = Number(item?.inv_item_value) || 0;

    if (isCustomVariant) {
        const baseCoin = itemValue;
        const bonusCoin =
            itemValue < 300000 ? 0 : itemValue < 1000000 ? baseCoin * 0.01 : baseCoin * 0.02;

        return {
            baseCoin,
            bonusCoin,
            totalCoin: baseCoin + bonusCoin,
        };
    }

    const baseCoin = Number(variant?.total_earned_coin) || 0;
    const bonusValue = Number(variant?.prod_var_bonus) || 0;
    const bonusType = Number(variant?.prod_var_bonus_type ?? 0);
    const bonusCoin = bonusType === 0 ? (baseCoin * bonusValue) / 100 : bonusValue;

    return {
        baseCoin,
        bonusCoin,
        totalCoin: baseCoin + bonusCoin,
    };
};

const statusBadgeClass = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "success" || normalized === "berhasil") {
        return "bg-green-100 text-green-700";
    }

    if (normalized === "pending") {
        return "bg-amber-100 text-amber-700";
    }

    if (normalized === "expired" || normalized === "failed") {
        return "bg-red-100 text-red-700";
    }

    return "bg-slate-100 text-slate-700";
};

export default function Detail({ data }) {
    const [paying, setPaying] = useState(false);
    const [sendingCoin, setSendingCoin] = useState(false);
    const [awaitingPaymentSuccess, setAwaitingPaymentSuccess] = useState(false);
    const [awaitingMomoSuccess, setAwaitingMomoSuccess] = useState(false);
    const invoice = data ?? {};
    const items = Array.isArray(invoice?.items) ? invoice.items : [];
    const trxId = invoice?.payment?.trx_id;
    const paymentStatus = Number(invoice?.payment?.payment_status ?? -1);
    const shouldShowPayButton = (!trxId || trxId === "-") && paymentStatus === 0;
    const momoStatus = String(invoice?.status_momo_transfer_label ?? "").toLowerCase();
    const shouldShowSendCoinButton = paymentStatus === 1 && momoStatus !== "success";

    useEffect(() => {
        if (awaitingPaymentSuccess && paymentStatus === 1) {
            window.location.reload();
        }
    }, [awaitingPaymentSuccess, paymentStatus]);

    useEffect(() => {
        if (awaitingMomoSuccess && momoStatus === "success") {
            window.location.reload();
        }
    }, [awaitingMomoSuccess, momoStatus]);

    useEffect(() => {
        if (!awaitingPaymentSuccess || paymentStatus === 1) return undefined;

        const intervalId = window.setInterval(() => {
            router.reload({
                only: ["data"],
                preserveScroll: true,
                preserveState: true,
            });
        }, 3000);

        return () => window.clearInterval(intervalId);
    }, [awaitingPaymentSuccess, paymentStatus]);

    useEffect(() => {
        if (!awaitingMomoSuccess || momoStatus === "success") return undefined;

        const intervalId = window.setInterval(() => {
            router.reload({
                only: ["data"],
                preserveScroll: true,
                preserveState: true,
            });
        }, 3000);

        return () => window.clearInterval(intervalId);
    }, [awaitingMomoSuccess, momoStatus]);

    const handleManualPay = async () => {
        if (!invoice?.inv_id || paying) return;

        try {
            setPaying(true);
            setAwaitingPaymentSuccess(true);
            await window.axios.post(`/panel/invoices/${invoice.inv_id}/manual-pay`);
            await router.reload({ only: ["data"] });
        } catch (error) {
            setAwaitingPaymentSuccess(false);
            const message =
                error?.response?.data?.message ??
                "Failed to process manual pay.";
            window.alert(message);
        } finally {
            setPaying(false);
        }
    };

    const handleManualSendCoin = async () => {
        if (!invoice?.inv_id || sendingCoin) return;

        try {
            setSendingCoin(true);
            setAwaitingMomoSuccess(true);
            await window.axios.post(`/panel/invoices/${invoice.inv_id}/manual-send-momo`);
            await router.reload({ only: ["data"] });
        } catch (error) {
            setAwaitingMomoSuccess(false);
            const message =
                error?.response?.data?.message ??
                "Failed to send coin manually.";
            window.alert(message);
        } finally {
            setSendingCoin(false);
        }
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div className="flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <h1 className="text-lg md:text-xl font-semibold text-foreground">Invoice Detail</h1>
                                <p className="text-sm text-muted-foreground-1">Complete invoice information and item breakdown.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => router.visit("/panel/invoices")}
                                className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover"
                            >
                                Back to Invoices
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Invoice Code</p>
                                <p className="mt-1 text-sm font-semibold text-foreground">{invoice.inv_code ?? "-"}</p>
                            </div>
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Sales Order</p>
                                <p className="mt-1 text-sm font-semibold text-foreground">{invoice.sales_order?.so_code ?? "-"}</p>
                            </div>
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Payment Code</p>
                                <p className="mt-1 text-sm font-semibold text-foreground">{invoice.payment?.pay_code ?? "-"}</p>
                            </div>
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Payment Method</p>
                                <p className="mt-1 text-sm font-semibold text-foreground">{invoice.invoice_payment_method_nama ?? "-"}</p>
                            </div>
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Created At</p>
                                <p className="mt-1 text-sm font-semibold text-foreground">{formatDate(invoice.created_at)}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Payment Status</p>
                                <div className="mt-1 flex items-center gap-2">
                                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(invoice.payment?.payment_status_label)}`}>
                                        {invoice.payment?.payment_status_label ?? "-"}
                                    </span>
                                    {shouldShowPayButton && (
                                        <button
                                            type="button"
                                            onClick={handleManualPay}
                                            disabled={paying}
                                            className="py-2.5 px-5 inline-flex items-center gap-x-2 text-sm font-semibold rounded-xl border border-fuchsia-300/70 bg-gradient-to-r from-fuchsia-500 via-purple-500 to-violet-500 text-white shadow-[0_0_20px_rgba(168,85,247,0.65)] hover:shadow-[0_0_28px_rgba(192,132,252,0.9)] hover:brightness-110 focus:outline-hidden focus:ring-2 focus:ring-fuchsia-300/70"
                                        >
                                            {paying ? "Processing..." : "Pay"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Momo Transfer Status</p>
                                <div className="mt-1 flex items-center gap-2">
                                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(invoice.status_momo_transfer_label)}`}>
                                        {invoice.status_momo_transfer_label ?? "-"}
                                    </span>
                                    {shouldShowSendCoinButton && (
                                        <button
                                            type="button"
                                            onClick={handleManualSendCoin}
                                            disabled={sendingCoin}
                                            className="py-1.5 px-3 inline-flex items-center gap-x-1.5 text-xs font-medium rounded-lg bg-primary border border-primary-line text-primary-foreground hover:bg-primary-hover disabled:opacity-50 disabled:pointer-events-none"
                                        >
                                            {sendingCoin ? "Sending..." : "Send Coin"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="bg-card border border-card-line rounded-xl shadow-2xs p-4">
                                <p className="text-xs text-muted-foreground-1">Payment Total</p>
                                <p className="mt-1 text-sm font-semibold text-foreground">
                                    {formatCurrency(invoice.payment?.pay_total)}
                                </p>
                            </div>
                        </div>

                        <div className="bg-card border border-card-line rounded-xl shadow-2xs p-5">
                            <h2 className="text-base font-semibold text-foreground mb-3">Invoice Items</h2>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-table-line">
                                    <thead>
                                        <tr>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Product</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Variant</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Value</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Merchant Fee</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Coin</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Bonus</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Total Coin</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-table-line">
                                        {items.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="py-6 px-4 text-center text-sm text-muted-foreground-2">
                                                    No invoice items found.
                                                </td>
                                            </tr>
                                        ) : (
                                            items.map((item) => {
                                                const coinSummary = calculateInvoiceItemCoin(item);

                                                return (
                                                <tr key={item.inv_it_id} className="hover:bg-surface">
                                                    <td className="py-3 px-4 text-sm text-foreground">
                                                        {item.variant?.product?.prod_name ?? "-"}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        <div className="flex items-center gap-2">
                                                            <span>{item.variant?.prod_var_name ?? "-"}</span>
                                                            {Number(item.variant?.prod_var_type ?? 0) === 1 && (
                                                                <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                                    Custom
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        {formatCurrency(item.inv_item_value)}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        {formatCurrency(item.payment_fee_nominal)}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        {formatCoin(coinSummary.baseCoin)}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        {formatCoin(coinSummary.bonusCoin)}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm font-medium text-foreground">
                                                        {formatCoin(coinSummary.totalCoin)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
